import { CashRegisterEntryData } from '../../models';

/**
 * Identity of the cash register being viewed/operated on.
 * A shared register is identified by `cashRegisterName`; a user's own
 * (personal) register by `username` with no `cashRegisterName`.
 */
export interface CashRegisterScope {
    cashRegisterName?: string;
    username?: string;
}

/**
 * Resolves the active cash register scope from the selected shared register
 * name (Redux `general.selectedCashRegisterName`) and the current username.
 * Empty/undefined selected name => the user's own register.
 */
export const resolveCashRegisterScope = (
    selectedCashRegisterName: string | undefined,
    username: string | undefined,
): CashRegisterScope =>
    selectedCashRegisterName
        ? { cashRegisterName: selectedCashRegisterName }
        : { username };

/**
 * Single source of truth for "does this entry belong to this register".
 * Shared register => match by name (all users). Own register => entries with
 * no register name that belong to the user.
 */
export const entryBelongsToScope = (
    e: CashRegisterEntryData,
    scope: CashRegisterScope,
): boolean =>
    scope.cashRegisterName
        ? e.cashRegisterName === scope.cashRegisterName
        : !e.cashRegisterName && e.username === scope.username;

/**
 * The payment types to display for a register: the configured payment methods
 * unioned with any type actually seen on the entries (so legacy/removed
 * methods still surface). Every configured method is always present even with
 * a zero balance.
 */
export const cashRegisterPaymentTypes = (
    entries: CashRegisterEntryData[],
    configuredMethods: string[] | undefined,
): string[] => {
    const seen = new Set<string>();
    (configuredMethods ?? []).forEach(m => seen.add(m));
    entries.forEach(e => seen.add(e.paymentType));
    return Array.from(seen).filter(Boolean);
};

/**
 * Balance for a payment type: the signed sum of every entry of that type
 * still pending (not yet handled) — the money physically in the register.
 */
export const pendingBalanceOf = (
    entries: CashRegisterEntryData[],
    type: string,
): number => entries
    .filter(e => e.paymentType === type && !e.handledByUser)
    .reduce((sum, e) => sum + e.paymentValue, 0);
